import { formatBytes } from '@/lib/format'

/**
 * Stacked horizontal breakdown of stored bytes into modules / state / providers.
 * Mirrors ResourceSplitBar's structure (rounded track + mono legend with swatches).
 *
 * `measured` gates the whole bar: when false (no recorded artifact sizes anywhere)
 * the empty grey track still renders (so the bar is always present) but the legend
 * shows a muted "not measured" caption instead of a misleading "0 B used".
 *
 * Each domain ALSO carries its own flag, because the aggregate one is true as soon as
 * any single domain has sizes. An org whose state is measured but whose modules all
 * predate capture-on-write would otherwise render "0 B modules" - a number that looks
 * like a fact and isn't. A domain flagged unmeasured shows "not measured" in its place.
 * Undefined (worker older than the per-domain split) falls back to the aggregate flag.
 *
 * WIDTHS ARE LOG-SCALED, so they show presence and rank, NOT share of total. Real orgs
 * span six orders of magnitude here - 47 GB of provider binaries beside 39 KB of module
 * tarballs - and on a linear scale the small domains round to nothing: a 39 KB segment on
 * a 300px bar is a quarter of one pixel. The exact bytes live in the legend beside each
 * swatch, which is what a reader should compare; the bar is the index into them. Labelled
 * "log scale" so the widths are never mistaken for proportions.
 */

/**
 * Floor on each visible segment, as a share of the track. The whole point of this bar is
 * that a domain you DO occupy is never invisible, and a log scale alone doesn't guarantee
 * that: log10 still sends a 24-byte module footprint beside gigabytes of providers down to
 * ~4% of the weight. Reserving the floor first and splitting only what's left by weight
 * keeps the segment clickable-sized and the total at exactly 100%.
 */
const MIN_SEGMENT_PCT = 6

/**
 * log10, offset so a 1-byte artifact still has positive weight (log10(1) is 0, which would
 * collapse back to an invisible segment). Zero-byte domains never reach here - they're
 * filtered out of the bar entirely, since "occupies nothing" is honestly nothing to draw.
 */
const logWeight = (bytes: number) => Math.log10(bytes + 1)

export function StorageBar({
  module,
  state,
  provider,
  measured,
  moduleMeasured,
  stateMeasured,
  providerMeasured,
}: {
  module: number
  state: number
  provider: number
  measured: boolean
  moduleMeasured?: boolean
  stateMeasured?: boolean
  providerMeasured?: boolean
}) {
  const height = 10
  const segs = [
    { key: 'modules', label: 'modules', bytes: module, color: 'var(--accent)', measured: moduleMeasured ?? measured },
    { key: 'state', label: 'state', bytes: state, color: 'var(--blue)', measured: stateMeasured ?? measured },
    { key: 'providers', label: 'providers', bytes: provider, color: 'var(--text2)', measured: providerMeasured ?? measured },
  ]
  // Bar shows only categories with recorded bytes (a 0-byte category has no width).
  const shown = segs.filter((s) => s.bytes > 0)
  const weightTotal = shown.reduce((sum, s) => sum + logWeight(s.bytes), 0)
  // Hand every visible segment the floor, then share the remainder by log weight. Guarded so
  // the floors can never overrun the track, and so an all-equal-weight bar still splits evenly.
  const free = Math.max(100 - MIN_SEGMENT_PCT * shown.length, 0)
  const pct = (bytes: number) =>
    MIN_SEGMENT_PCT +
    (weightTotal > 0 ? (free * logWeight(bytes)) / weightTotal : free / Math.max(shown.length, 1))

  // Legend always includes `modules` (even at 0 B) so the module footprint is explicit,
  // then any other category that has bytes. Deduped so modules isn't listed twice.
  const legend = [
    segs[0],
    ...shown.filter((s) => s.key !== 'modules'),
  ]
  const describe = (s: { bytes: number; measured: boolean }) =>
    s.measured ? formatBytes(s.bytes) : 'not measured'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div
        role="img"
        aria-label={
          measured
            ? `Storage, log scale: ${segs.map((s) => `${describe(s)} ${s.label}`).join(', ')}`
            : 'Storage not yet measured'
        }
        // 2px gaps let the track show through between fills, so adjacent segments read as
        // separate marks without a border - and without leaning on hue alone to separate them.
        style={{ display: 'flex', gap: 2, height, borderRadius: height / 2, overflow: 'hidden', background: 'var(--bg3)' }}
      >
        {measured &&
          shown.map((s) => (
            <div
              key={s.key}
              // Exact bytes on hover: the segment is 10px tall and log-scaled, so its width is
              // never the thing to read a value off.
              title={`${describe(s)} ${s.label}`}
              style={{ flex: `0 1 ${pct(s.bytes)}%`, minWidth: 3, background: s.color }}
            />
          ))}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontFamily: 'var(--mono)', fontSize: '0.66rem' }}>
        {measured ? (
          <>
            {legend.map((s) => (
              <span key={s.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: s.measured ? 'var(--text2)' : 'var(--text3)' }}>
                <span style={{ width: 7, height: 7, borderRadius: 2, background: s.measured ? s.color : 'var(--bg3)', flexShrink: 0 }} />
                {describe(s)} {s.label}
              </span>
            ))}
            {/* Annotation, not a legend entry - the leading dot keeps it reading as an aside when
                the legend wraps. Only meaningful once there are proportions to misread. */}
            {shown.length > 1 && <span className="dim">· log scale</span>}
          </>
        ) : (
          <span className="dim">not measured</span>
        )}
      </div>
    </div>
  )
}
